/**
 * LensSelector - Multi-Lens Selection Component
 * ============================================================================
 * DC-1040: Component for selecting which governance lenses to run
 *
 * Features:
 * - Grid of 6 checkboxes with lens icons and names
 * - "Select All" / "Deselect All" toggle button
 * - Each lens shows: icon, name, brief description
 * - Disabled state grays out checkboxes
 *
 * @version 1.0.0
 * @created 2026-01-25
 * ============================================================================
 */

import React, { useCallback, useMemo } from 'react';
import {
  type GovernanceLensId,
  type LensInfo,
  LENS_CONFIG,
  ALL_LENS_IDS,
} from './types';
import './LensSelector.css';

// ============================================================================
// Types
// ============================================================================

export interface LensSelectorProps {
  /** Available lenses (from API or default to all) */
  availableLenses?: LensInfo[];
  /** Currently selected lens IDs */
  selectedLenses: GovernanceLensId[];
  /** Callback when selection changes */
  onSelectionChange: (lenses: GovernanceLensId[]) => void;
  /** Disable all interactions */
  disabled?: boolean;
  /** Compact mode for smaller spaces */
  compact?: boolean;
  /** Show descriptions */
  showDescriptions?: boolean;
  /** Custom className */
  className?: string;
}

// ============================================================================
// Helper Components
// ============================================================================

interface LensCheckboxProps {
  lensId: GovernanceLensId;
  lensInfo?: LensInfo;
  selected: boolean;
  disabled: boolean;
  compact: boolean;
  showDescription: boolean;
  onToggle: () => void;
}

const LensCheckbox: React.FC<LensCheckboxProps> = ({
  lensId,
  lensInfo,
  selected,
  disabled,
  compact,
  showDescription,
  onToggle,
}) => {
  const config = LENS_CONFIG[lensId] || {
    name: lensId,
    shortName: lensId.substring(0, 4).toUpperCase(),
    icon: lensId.charAt(0).toUpperCase(),
    color: '#6B7280',
    bgColor: '#F3F4F6',
  };
  const description = lensInfo?.description || getDefaultDescription(lensId);

  return (
    <label
      className={`gls-checkbox ${selected ? 'gls-checkbox--selected' : ''} ${
        disabled ? 'gls-checkbox--disabled' : ''
      } ${compact ? 'gls-checkbox--compact' : ''}`}
      style={{
        '--lens-color': disabled ? '#6B7280' : config.color,
        '--lens-bg': disabled ? '#F3F4F6' : config.bgColor,
      } as React.CSSProperties}
    >
      <input
        type="checkbox"
        checked={selected}
        disabled={disabled}
        onChange={onToggle}
        className="gls-checkbox__input"
      />

      <span className="gls-checkbox__icon">{config.icon}</span>

      <span className="gls-checkbox__content">
        <span className="gls-checkbox__name">{config.name}</span>
        {showDescription && !compact && (
          <span className="gls-checkbox__description">{description}</span>
        )}
      </span>

      <span className="gls-checkbox__checkmark">
        {selected && (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        )}
      </span>
    </label>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const LensSelector: React.FC<LensSelectorProps> = ({
  availableLenses,
  selectedLenses,
  onSelectionChange,
  disabled = false,
  compact = false,
  showDescriptions = true,
  className = '',
}) => {
  // Build lens list from availableLenses or use defaults
  const lensIds = useMemo(() => {
    if (availableLenses?.length) {
      // Handle both 'id' and 'lens_id' field names from API
      return availableLenses
        .map(l => (l.lens_id || (l as unknown as { id?: string }).id) as GovernanceLensId)
        .filter(Boolean);
    }
    return ALL_LENS_IDS;
  }, [availableLenses]);

  const lensInfoMap = useMemo(() => {
    const map: Record<string, LensInfo> = {};
    if (availableLenses) {
      for (const lens of availableLenses) {
        const id = lens.lens_id || (lens as unknown as { id?: string }).id;
        if (id) {
          map[id] = { ...lens, lens_id: id };
        }
      }
    }
    return map;
  }, [availableLenses]);

  // Check if all lenses are selected
  const allSelected = useMemo(() => {
    return lensIds.every(id => selectedLenses.includes(id));
  }, [lensIds, selectedLenses]);

  // Check if any lenses are selected
  const anySelected = selectedLenses.length > 0;

  /**
   * Toggle a single lens
   */
  const handleToggle = useCallback(
    (lensId: GovernanceLensId) => {
      if (selectedLenses.includes(lensId)) {
        onSelectionChange(selectedLenses.filter(id => id !== lensId));
      } else {
        onSelectionChange([...selectedLenses, lensId]);
      }
    },
    [selectedLenses, onSelectionChange]
  );

  /**
   * Select/Deselect all lenses
   */
  const handleToggleAll = useCallback(() => {
    if (allSelected) {
      onSelectionChange([]);
    } else {
      onSelectionChange([...lensIds]);
    }
  }, [allSelected, lensIds, onSelectionChange]);

  return (
    <div className={`gls-selector ${compact ? 'gls-selector--compact' : ''} ${className}`}>
      {/* Header with Select All */}
      <div className="gls-selector__header">
        <span className="gls-selector__title">
          Select Lenses to Analyze
          {anySelected && (
            <span className="gls-selector__count">
              ({selectedLenses.length}/{lensIds.length})
            </span>
          )}
        </span>
        <button
          type="button"
          className={`gls-toggle-all ${allSelected ? 'gls-toggle-all--deselect' : ''}`}
          onClick={handleToggleAll}
          disabled={disabled}
        >
          {allSelected ? 'Deselect All' : 'Select All'}
        </button>
      </div>

      {/* Lens Grid */}
      <div className={`gls-grid ${compact ? 'gls-grid--compact' : ''}`}>
        {lensIds.map(lensId => (
          <LensCheckbox
            key={lensId}
            lensId={lensId}
            lensInfo={lensInfoMap[lensId]}
            selected={selectedLenses.includes(lensId)}
            disabled={disabled}
            compact={compact}
            showDescription={showDescriptions}
            onToggle={() => handleToggle(lensId)}
          />
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// Helper Functions
// ============================================================================

function getDefaultDescription(lensId: GovernanceLensId): string {
  const descriptions: Record<GovernanceLensId, string> = {
    fair: 'Evaluate FAIR data principles coverage',
    ml_readiness: 'Check ML governance policies',
    privacy_compliance: 'Assess GDPR, HIPAA, CCPA compliance',
    regulatory: 'Verify regulatory standards adherence',
    data_quality: 'Review data quality requirements',
    financial_services: 'Check SR 11-7 model risk requirements',
  };
  return descriptions[lensId];
}

export default LensSelector;
